import React, { useEffect, useMemo } from 'react';
import { useReportes } from './ReportesContex';
import Chart from 'react-apexcharts';
import { ChartBar, AlertTriangle, Target } from 'tabler-icons-react';
import '../../styles/reportes.css';

function getPrimaryColor() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--color').trim();
    return raw.startsWith('#') ? raw.slice(1) : raw;
}

function blendHex(hex, blendWith, ratio) {
    const parse = (h) => [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    const [r1, g1, b1] = parse(hex.padStart(6, '0'));
    const [r2, g2, b2] = parse(blendWith.padStart(6, '0'));
    const r = Math.round(r1 * ratio + r2 * (1 - ratio));
    const g = Math.round(g1 * ratio + g2 * (1 - ratio));
    const b = Math.round(b1 * ratio + b2 * (1 - ratio));
    return [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}

async function exportarExcel(sumParos, pareto, selector, selectedArea, selectedLinea) {
    const ExcelJS = (await import('exceljs')).default;
    const { saveAs } = await import('file-saver');

    const toArgb  = (hex) => `FF${hex.toUpperCase()}`;
    const primary  = getPrimaryColor();
    const headerBg = blendHex(primary, '000000', 0.8);
    const infoBg   = blendHex(primary, 'ffffff', 0.85);

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Pareto');

    const infoText = [
        selectedArea?.nombre  ? `Área: ${selectedArea.nombre}`   : '',
        selectedLinea?.nombre ? `Línea: ${selectedLinea.nombre}` : '',
    ].filter(Boolean).join('   |   ');
    if (infoText) {
        ws.mergeCells('A1:D1');
        ws.getCell('A1').value = infoText;
        ws.getCell('A1').style = { font: { size: 11 }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(infoBg) } } };
        ws.getRow(1).height = 18;
    }

    const hdrStyle = { font: { bold: true, color: { argb: 'FFFFFFFF' } }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(headerBg) } }, alignment: { horizontal: 'center', vertical: 'middle' } };
    const valLabel = selector === 0 ? 'Minutos' : 'Incidencias';
    const hdrs = ['Causa', valLabel, '% Individual', '% Acumulado'];
    const hdrRow = ws.getRow(infoText ? 2 : 1);
    hdrs.forEach((h, i) => { hdrRow.getCell(i + 1).value = h; hdrRow.getCell(i + 1).style = hdrStyle; });
    hdrRow.height = 20;
    [32, 14, 14, 14].forEach((w, i) => { ws.getColumn(i + 1).width = w; });

    const total = pareto.total;
    const dataRow = infoText ? 3 : 2;
    pareto.labels.forEach((label, i) => {
        const row = ws.getRow(dataRow + i);
        const val = pareto.values[i];
        row.getCell(1).value = label;
        row.getCell(2).value = val;
        row.getCell(3).value = total > 0 ? parseFloat(((val / total) * 100).toFixed(1)) : 0;
        row.getCell(4).value = pareto.cumulative[i];
        row.getCell(3).numFmt = '0.0"%"';
        row.getCell(4).numFmt = '0.0"%"';
        if (i % 2 !== 0) {
            row.eachCell((c) => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF7F8FE' } }; });
        }
        row.height = 16;
    });

    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `pareto_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

export default function Informe2() {
    const { sumParos, selector, setCelda, selectedArea, selectedLinea, setExportFn } = useReportes();

    const pareto = useMemo(() => {
        if (!sumParos.length) {
            return { labels: [], values: [], cumulative: [], total: 0, topLabel: '', topPct: 0, causasHasta80: 0 };
        }

        const sorted = [...sumParos].sort((a, b) => b.valor - a.valor);
        const total = sorted.reduce((acc, p) => acc + p.valor, 0);

        let acum = 0;
        const cumulative = sorted.map((p) => {
            acum += p.valor;
            return total > 0 ? Math.round((acum / total) * 1000) / 10 : 0;
        });

        let causasHasta80 = 0;
        for (let i = 0; i < cumulative.length; i++) {
            causasHasta80++;
            if (cumulative[i] >= 80) break;
        }

        return {
            labels: sorted.map((p) => p.paro),
            values: sorted.map((p) => p.valor),
            cumulative,
            total,
            topLabel: sorted[0]?.paro ?? '',
            topPct: total > 0 ? Math.round((sorted[0]?.valor / total) * 100) : 0,
            causasHasta80,
        };
    }, [sumParos]);

    const series = [
        {
            name: selector === 0 ? 'Minutos' : 'Incidencias',
            type: 'bar',
            data: pareto.values,
        },
        {
            name: '% Acumulado',
            type: 'line',
            data: pareto.cumulative,
        },
    ];

    const options = useMemo(() => ({
        chart: {
            type: 'bar',
            toolbar: { show: false },
            zoom: { enabled: false },
            fontFamily: 'WorkSans',
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 600,
                animateGradually: { enabled: true, delay: 80 },
                dynamicAnimation: { enabled: true, speed: 400 },
            },
        },
        stroke: { width: [0, 3], curve: 'smooth' },
        colors: ['#6495ED', '#fe424d'],
        plotOptions: {
            bar: { columnWidth: '55%', borderRadius: 3 },
        },
        dataLabels: { enabled: false },
        markers: { size: [0, 5], colors: ['transparent', '#fe424d'] },
        xaxis: {
            categories: pareto.labels,
            labels: {
                rotate: -35,
                rotateAlways: true,
                style: {
                    fontSize: '0.75rem',
                    fontFamily: 'WorkSans',
                    colors: '#5f606f',
                },
                maxHeight: 120,
                formatter: (val) => (val && val.length > 22 ? `${val.slice(0, 22)}…` : val),
            },
        },
        yaxis: [
            {
                title: {
                    text: selector === 0 ? 'Minutos' : 'Incidencias',
                    style: { fontSize: '0.8125rem', fontFamily: 'WorkSans', color: '#5f606f', fontWeight: 600 },
                },
                labels: {
                    style: { colors: '#5f606f', fontFamily: 'WorkSans', fontSize: '0.75rem' },
                },
            },
            {
                opposite: true,
                min: 0,
                max: 100,
                tickAmount: 5,
                title: {
                    text: '% Acumulado',
                    style: { fontSize: '0.8125rem', fontFamily: 'WorkSans', color: '#5f606f', fontWeight: 600 },
                },
                labels: {
                    formatter: (val) => `${val}%`,
                    style: { colors: '#5f606f', fontFamily: 'WorkSans', fontSize: '0.75rem' },
                },
            },
        ],
        annotations: {
            yaxis: [
                {
                    y: 80,
                    yAxisIndex: 1,
                    borderColor: '#fe424d',
                    strokeDashArray: 6,
                    borderWidth: 2,
                    label: {
                        text: '80 %',
                        position: 'right',
                        style: {
                            color: '#fe424d',
                            background: '#fff5f5',
                            fontSize: '0.75rem',
                            fontFamily: 'WorkSans',
                            fontWeight: 700,
                        },
                    },
                },
            ],
        },
        tooltip: {
            shared: true,
            intersect: false,
            y: [
                { formatter: (val) => (selector === 0 ? `${val} min` : `${val} incidencias`) },
                { formatter: (val) => `${val} %` },
            ],
        },
        noData: {
            text: 'No existen datos para esta selección',
            align: 'center',
            verticalAlign: 'middle',
            style: { fontSize: '1.125rem', fontFamily: 'WorkSans', fontWeight: 600, color: '#5f606f' },
        },
        grid: { borderColor: '#e9ecf0' },
        legend: {
            position: 'top',
            horizontalAlign: 'right',
            fontFamily: 'WorkSans',
            fontSize: '0.8125rem',
        },
    }), [pareto.labels, selector]);

    useEffect(() => {
        setCelda(7);
    }, [setCelda]);

    useEffect(() => {
        if (pareto.total > 0) {
            setExportFn(() => () => exportarExcel(sumParos, pareto, selector, selectedArea, selectedLinea));
        } else {
            setExportFn(null);
        }
        return () => setExportFn(null);
    }, [pareto, sumParos, selector, selectedArea, selectedLinea, setExportFn]);

    return (
        <div className="informe2-container">
            {pareto.total > 0 && (
                <div className="pareto-kpi-row">
                    <div className="pareto-kpi-card">
                        <div className="pareto-kpi-card-icon pareto-kpi-card-icon--blue">
                            <ChartBar size={20} strokeWidth={2} />
                        </div>
                        <div className="pareto-kpi-card-body">
                            <span className="pareto-kpi-card-value">
                                {pareto.total.toLocaleString()}
                            </span>
                            <span className="pareto-kpi-card-label">
                                {selector === 0 ? 'Minutos totales' : 'Incidencias totales'}
                            </span>
                        </div>
                    </div>

                    <div className="pareto-kpi-card">
                        <div className="pareto-kpi-card-icon pareto-kpi-card-icon--red">
                            <AlertTriangle size={20} strokeWidth={2} />
                        </div>
                        <div className="pareto-kpi-card-body">
                            <span className="pareto-kpi-card-value">{pareto.topPct} %</span>
                            <span className="pareto-kpi-card-label">
                                causa principal · {pareto.topLabel}
                            </span>
                        </div>
                    </div>

                    <div className="pareto-kpi-card">
                        <div className="pareto-kpi-card-icon pareto-kpi-card-icon--orange">
                            <Target size={20} strokeWidth={2} />
                        </div>
                        <div className="pareto-kpi-card-body">
                            <span className="pareto-kpi-card-value">{pareto.causasHasta80}</span>
                            <span className="pareto-kpi-card-label">
                                {pareto.causasHasta80 === 1 ? 'causa cubre' : 'causas cubren'} el 80 %
                            </span>
                        </div>
                    </div>
                </div>
            )}

            <div className="informe2-title">
                <h3>
                    {selector === 0
                        ? 'Fallas por Tiempo (min)'
                        : 'Fallas por Incidencias'}
                </h3>
            </div>

            <div className="informe2-chart-wrapper">
                <Chart options={options} series={series} type="bar" width="100%" height={420} />
            </div>
        </div>
    );
}
